import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/authContext';
import { morgansClient } from '../../../lib/connect/clients';
import { PORTFOLIO_ECONOMICS_KEY } from './usePortfolioEconomics';
const LANDSCAPES_KEY = [
    'landscapes'
];
const landscapeKey = (id)=>[
        'landscapes',
        'one',
        id
    ];
const membersKey = (id, includeRemoved)=>[
        'landscapes',
        'members',
        id,
        includeRemoved
    ];
const historyKey = (id)=>[
        'landscapes',
        'history',
        id
    ];
/** Every Landscape of the tenant; a viewer without opportunity read sees counts of 0. */ export function useLandscapes() {
    const { user } = useAuth();
    const identity = [
        user?.tenant_id ?? '',
        user?.id ?? ''
    ];
    const enabled = !!user?.tenant_id && !!user?.id;
    const query = useQuery({
        queryKey: [
            ...LANDSCAPES_KEY,
            ...identity
        ],
        enabled,
        queryFn: async ()=>(await morgansClient.listLandscapes({})).landscapes
    });
    return {
        ...query,
        data: enabled && !query.isError ? query.data : undefined
    };
}
export function useLandscape(id) {
    const { user } = useAuth();
    const identity = [
        user?.tenant_id ?? '',
        user?.id ?? ''
    ];
    const enabled = !!user?.tenant_id && !!user?.id;
    const query = useQuery({
        queryKey: [
            ...landscapeKey(id),
            ...identity
        ],
        queryFn: async ()=>(await morgansClient.getLandscape({
                id
            })).landscape,
        enabled: enabled && !!id
    });
    return {
        ...query,
        data: enabled && !query.isError ? query.data : undefined
    };
}
export function useSaveLandscape(id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const response = id ? await morgansClient.updateLandscape({
                id,
                ...input
            }) : await morgansClient.createLandscape(input);
            return response.landscape;
        },
        onSettled: ()=>queryClient.invalidateQueries({
                queryKey: LANDSCAPES_KEY
            })
    });
}
export function useLandscapeMembers(landscapeId, includeRemoved) {
    const { user } = useAuth();
    const identity = [
        user?.tenant_id ?? '',
        user?.id ?? ''
    ];
    const enabled = !!user?.tenant_id && !!user?.id;
    const query = useQuery({
        queryKey: [
            ...membersKey(landscapeId, includeRemoved),
            ...identity
        ],
        queryFn: async ()=>(await morgansClient.listLandscapeMembers({
                landscapeId,
                includeRemoved
            })).members,
        enabled: enabled && !!landscapeId
    });
    return {
        ...query,
        data: enabled && !query.isError ? query.data : undefined
    };
}
export function useLandscapeStory(landscapeId, includeRemoved) {
    const { user } = useAuth();
    const identity = [
        user?.tenant_id ?? '',
        user?.id ?? ''
    ];
    const enabled = !!user?.tenant_id && !!user?.id;
    const query = useQuery({
        queryKey: [
            'landscapes',
            'story',
            landscapeId,
            includeRemoved,
            ...identity
        ],
        queryFn: ()=>morgansClient.getLandscapeStory({
                landscapeId,
                includeRemoved
            }),
        enabled: enabled && !!landscapeId
    });
    return {
        ...query,
        data: enabled && !query.isError ? query.data : undefined
    };
}
export function useLandscapeHistory(landscapeId) {
    const { user } = useAuth();
    const identity = [
        user?.tenant_id ?? '',
        user?.id ?? ''
    ];
    const enabled = !!user?.tenant_id && !!user?.id;
    const query = useQuery({
        queryKey: [
            ...historyKey(landscapeId),
            ...identity
        ],
        queryFn: async ()=>(await morgansClient.listLandscapeMemberHistory({
                landscapeId
            })).events,
        enabled: enabled && !!landscapeId
    });
    return {
        ...query,
        data: enabled && !query.isError ? query.data : undefined
    };
}
/** Add / remove / run-rules all change members, counts and history together. */ export function useLandscapeMemberActions(landscapeId) {
    const queryClient = useQueryClient();
    // Membership changes the deduplicated economics too.
    const invalidate = ()=>Promise.all([
            queryClient.invalidateQueries({
                queryKey: LANDSCAPES_KEY
            }),
            queryClient.invalidateQueries({
                queryKey: PORTFOLIO_ECONOMICS_KEY
            }),
            queryClient.invalidateQueries({
                queryKey: [
                    'landscape-strategy',
                    landscapeId
                ]
            })
        ]);
    const add = useMutation({
        mutationFn: (opportunityId)=>morgansClient.addLandscapeMember({
                landscapeId,
                opportunityId,
                reason: ''
            }),
        onSettled: invalidate
    });
    const remove = useMutation({
        mutationFn: ({ opportunityId, reason })=>morgansClient.removeLandscapeMember({
                landscapeId,
                opportunityId,
                reason
            }),
        onSettled: invalidate
    });
    const runRules = useMutation({
        mutationFn: ()=>morgansClient.runLandscapeRules({
                landscapeId
            }),
        onSettled: invalidate
    });
    return {
        add,
        remove,
        runRules
    };
}
